import { useState } from 'react';
import PropTypes from 'prop-types';
import { Key, Eye, EyeOff } from 'lucide-react';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import { toast } from '../../components/common/Toast';
import { authApi } from '../../services/api';
import './ChangePasswordModal.css';

export default function ChangePasswordModal({ isOpen, onClose }) {
    const [form, setForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const togglePassword = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const validateForm = () => {
        if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
            setError('All fields are required');
            return false;
        }
        if (form.newPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return false;
        }
        if (form.newPassword !== form.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        if (form.currentPassword === form.newPassword) {
            setError('New password must be different from current password');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            await authApi.changePassword({
                currentPassword: form.currentPassword,
                newPassword: form.newPassword
            });
            toast.success('Password changed successfully');
            handleClose();
        } catch (err) {
            setError(err.response?.data?.error?.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswords({ current: false, new: false, confirm: false });
        setError('');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Change Password"
            size="sm"
        >
            <form onSubmit={handleSubmit} className="change-password-form">
                {error && (
                    <Alert type="error" className="change-password-alert">
                        {error}
                    </Alert>
                )}

                <div className="password-field">
                    <Input
                        name="currentPassword"
                        type={showPasswords.current ? 'text' : 'password'}
                        label="Current Password"
                        value={form.currentPassword}
                        onChange={handleChange}
                        icon={Key}
                        required
                    />
                    <button
                        type="button"
                        className="password-toggle"
                        onClick={() => togglePassword('current')}
                    >
                        {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>

                <div className="password-field">
                    <Input
                        name="newPassword"
                        type={showPasswords.new ? 'text' : 'password'}
                        label="New Password"
                        value={form.newPassword}
                        onChange={handleChange}
                        icon={Key}
                        helperText="Minimum 8 characters"
                        required
                    />
                    <button
                        type="button"
                        className="password-toggle"
                        onClick={() => togglePassword('new')}
                    >
                        {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>

                <div className="password-field">
                    <Input
                        name="confirmPassword"
                        type={showPasswords.confirm ? 'text' : 'password'}
                        label="Confirm New Password"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        icon={Key}
                        required
                    />
                    <button
                        type="button"
                        className="password-toggle"
                        onClick={() => togglePassword('confirm')}
                    >
                        {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>

                <div className="change-password-actions">
                    <Button variant="secondary" type="button" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={loading}>
                        Change Password
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

ChangePasswordModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};
